import {
  BitmapText,
  Text,
  Container,
  FederatedPointerEvent,
  NineSliceSprite,
  Rectangle,
  TextStyleOptions,
  Texture,
} from "pixi.js";
import { FontName, soundMan } from "../logic";

/**
 * Button constructor options.
 */
export interface ButtonOptions {
  caption?: string;

  /**
   * caption text style.
   */
  style?: Partial<TextStyleOptions>;

  x?: number;
  y?: number;
  width?: number;
  height?: number;
  textureName?: string;

  /**
   * shortcut for single value applied to leftWidth, topHeight, rightWidth and bottomHeight.
   */
  ltrbSize?: number;

  leftWidth?: number;
  topHeight?: number;
  rightWidth?: number;
  bottomHeight?: number;

  onClick?: (event: Event | FederatedPointerEvent) => void;
  /**
   * Is the texture an atlas with normal, hover and pressed sub textures or a whole overlay for the button. Default is true.
   */
  isAtlas?: boolean;

  highlightTint?: number;

  captionStart?: number;

  frame?: Rectangle;
}

/**
 * Sprite button created from texture atlas.
 * The atlas is divided into three stacked sub textures, each representing normal, hover and pressed button texture.
 */
export class Button extends Container {
  /**
   * Standard button texture.
   */
  private textureMain!: Texture;

  /**
   * Highlighted or hover texture.
   */
  private textureHighlight!: Texture;

  private nineSliceSprite: NineSliceSprite;

  /**
   * Button pressed texture.
   */
  private textureDown!: Texture;
  private _isHighlighted: boolean = false;
  private _isPressed: boolean = false;
  private _isDisabled: boolean = false;
  private _isClickStarted: boolean = false;
  private text!: Text;

  private _isAtlas: boolean;

  constructor(private opt: ButtonOptions) {
    super();

    //  default styles
    opt.style ||= {};
    opt.style = {
      ...opt.style,
      fontSize: opt.style.fontSize || 12,
      fontFamily: opt.style.fontFamily || FontName.StandardButton,
    };
    opt.highlightTint ||= 0xa0ffa0;
    opt.textureName ||= "/assets/gui/btn-default.png";
    opt.ltrbSize ||= 15;
    opt.caption ||= "";
    opt.captionStart ||= 0;

    this.nineSliceSprite = new NineSliceSprite({
      texture: Texture.WHITE,
      leftWidth: opt.leftWidth || opt.ltrbSize,
      topHeight: opt.topHeight || opt.ltrbSize,
      rightWidth: opt.rightWidth || opt.ltrbSize,
      bottomHeight: opt.bottomHeight || opt.ltrbSize,
      roundPixels: true,
    });

    this.addChild(this.nineSliceSprite);
    this.position.set(opt.x || 0, opt.y || 0);

    this.onClick = opt.onClick;
    this._isAtlas = opt.isAtlas == undefined ? true : opt.isAtlas;

    //  setup button textures
    this.setTexture(opt.textureName);

    this.nineSliceSprite.width = opt.width || this.textureMain.width;
    this.nineSliceSprite.height = opt.height || this.textureMain.height;

    this.text = new Text({ text: opt.caption, style: opt.style });
    this.text.anchor.set(0);
    let tx = opt.captionStart!;
    switch (opt.style.align) {
      case "left":
        tx = opt.captionStart!;
        break;
      default:
        tx +=
          (this.nineSliceSprite.width - opt.captionStart! - this.text.width) /
          2;
        break;
    }
    this.text.position.set(
      tx,
      (this.nineSliceSprite.height - this.text.height) / 2,
    );
    this.addChild(this.text);

    this.eventMode = "static";
    this.cursor = "pointer";

    // set the mousedown and touchstart callback...
    this.on("pointerdown", this.onButtonDown);
    this.on("pointerup", this.onButtonUp);
    this.on("pointerupoutside", this.onButtonUpOutside);
    this.on("pointerover", this.onButtonOver);
    this.on("pointerout", this.onButtonOut);

    this.isPressed = false;
  }
  public get tint() {
    return this.nineSliceSprite.tint;
  }
  public set tint(color: number) {
    this.nineSliceSprite.tint = color;
  }

  public set enabled(value) {
    this.disabled = !value;
  }
  public get enabled() {
    return !this._isDisabled;
  }

  public get disabled() {
    return this._isDisabled;
  }
  public set disabled(state: boolean) {
    if (state != this._isDisabled) {
      this._isDisabled = state;
      this.cursor = state ? "default" : "pointer";
      this.applyTexture();
    }
  }

  public get isPressed() {
    return this._isPressed;
  }
  public set isPressed(state: boolean) {
    if (state != this._isPressed) {
      this._isPressed = state;
      this.applyTexture();
    }
  }

  public get isHighlighted() {
    return this._isHighlighted;
  }
  public set isHighlighted(state: boolean) {
    if (state != this._isHighlighted) {
      this._isHighlighted = state;
      this.applyTexture();
    }
  }
  public get captionWidth() {
    return this.text.width;
  }

  public get nspWidth() {
    return this.nineSliceSprite.width;
  }
  public set nspWidth(value: number) {
    this.nineSliceSprite.width = value;
    this.text.position.set(
      (this.nineSliceSprite.width - this.text.width) / 2,
      (this.nineSliceSprite.height - this.text.height) / 2,
    );
  }

  public get caption() {
    return this.text.text;
  }
  public set caption(value: string) {
    this.text.text = value;
    this.text.position.set(
      (this.nineSliceSprite.width - this.text.width) / 2,
      (this.nineSliceSprite.height - this.text.height) / 2,
    );
  }

  public onClick?: (event: Event | FederatedPointerEvent) => void;
  public pointerOver!: (event: FederatedPointerEvent) => void;
  public pointerOut!: (event: FederatedPointerEvent) => void;

  private onClickHandler = (event: Event | FederatedPointerEvent) => {
    if(!(event as any).suppressSound) soundMan.playSound("btn");
    if (this.onClick) {
      this.onClick(event);
    } else {
      console.warn("onClick() empty, did you forget to attach a handler?");
    }
  }

  private onButtonDown = () => {
    if (this._isDisabled) return;
    this._isClickStarted = true;
    if (this._isAtlas) this.nineSliceSprite.texture = this.textureDown;
  };

  private onButtonUp = (event: FederatedPointerEvent) => {
    if (this._isDisabled) return;
    if (this._isClickStarted) {
      this._isClickStarted = false;
      this.onClickHandler(event);
    }
    this.applyTexture();
  };

  private onButtonUpOutside = () => {
    if (this._isDisabled) return;
    this.applyTexture();
    this._isClickStarted = false;
  };

  private onButtonOver = (event: FederatedPointerEvent) => {
    if (this._isDisabled) return;
    if (this._isAtlas) 
      this.nineSliceSprite.texture = this.textureHighlight;
    else
      this.nineSliceSprite.tint = this.opt.highlightTint!;
    if (!!this.pointerOver) this.pointerOver(event);
  };

  private onButtonOut = (event: FederatedPointerEvent) => {
    if (this._isDisabled) return;
    this._isClickStarted = false;
    this.applyTexture();
    if (!!this.pointerOut) this.pointerOut(event);
  };

  private applyTexture() {
    if (this._isDisabled) {
      if (this._isAtlas) this.nineSliceSprite.texture = this.textureMain;
      this.nineSliceSprite.tint = 0x555555;
      this.nineSliceSprite.filters = [];
      if (this.text) this.text.tint = 0x555555;
    } else if (this._isHighlighted) {
      if (this._isAtlas) this.nineSliceSprite.texture = this.textureHighlight;
      this.nineSliceSprite.tint = this.opt.highlightTint!;
      this.nineSliceSprite.filters = [];
      if (this.text) this.text.tint = this.opt.highlightTint!;
    } else {
      if (this._isAtlas) {
        this.nineSliceSprite.texture = this._isPressed
          ? this.textureDown
          : this.textureMain;
      }
      this.nineSliceSprite.tint = 0xffffff;
      this.nineSliceSprite.filters = [];
      if (this.text) this.text.tint = 0xffffff;
    }
  }

  public performClick(event: Event | FederatedPointerEvent) {
    (event as any).suppressSound = true;
    this.onClickHandler(event);
  }

  public setTexture(textureAtlasName: string) {
    var atlasTexture = Texture.from(textureAtlasName);
    if (this._isAtlas) {
      atlasTexture.source.scaleMode = "nearest";
      const btnHeight = atlasTexture.height / 3;
      const btnWidth = atlasTexture.width;

      let frame = new Rectangle(
        atlasTexture.frame.x,
        atlasTexture.frame.y,
        btnWidth,
        btnHeight,
      );
      this.textureMain = new Texture({
        source: atlasTexture.source,
        frame: frame,
      });

      frame = new Rectangle(
        atlasTexture.frame.x,
        atlasTexture.frame.y + btnHeight,
        btnWidth,
        btnHeight,
      );
      this.textureHighlight = new Texture({
        source: atlasTexture.source,
        frame: frame,
      });

      frame = new Rectangle(
        atlasTexture.frame.x,
        atlasTexture.frame.y + btnHeight * 2,
        btnWidth,
        btnHeight,
      );
      this.textureDown = new Texture({
        source: atlasTexture.source,
        frame: frame,
      });
    } else {
      if(this.opt.frame) {
        atlasTexture = new Texture({
          source: atlasTexture.source,
          frame: this.opt.frame,
        });
      }
      //atlasTexture.source.scaleMode = "linear";
      this.nineSliceSprite.texture = atlasTexture;
    }
    this.applyTexture();
    //atlasTexture.source.scaleMode = 'linear';
  }
}
